import { GridApi } from "ag-grid-community";
import { ReactNode, useCallback, useEffect, useRef, useState } from "react";
import { useQueryClient } from "react-query";
import { useParams } from "react-router";
import { Box, Heading, Tab, TabList, TabPanel, TabPanels, Tabs, Tooltip } from "@chakra-ui/react";
import CustomBox from "../../components/Box";
import Button from "../../components/Button";
import DownloadMenu from "../../components/DownloadMenu";
import Modal from "../../components/Modal";
import useModal from "../../components/Modal/hooks/useModal";
import ToggleFilter from "../../components/ToggleFilter";
import { Option } from "../../components/ToggleFilter/types";
import downloadFile from "../../api/downloadFile";
import { DstSchemaItem, FilterOptionCounts, PipelineImportRow, PipelineImport as PipelineImportType, QueryFilter } from "../../api/types";
import useGetPipelineImport from "../../api/useGetPipelineImport";
import useEntitySelection from "../../hooks/useEntitySelection";
import useWebSocketMessage from "../../hooks/useWebSocketMessage";
import useThemeStore from "../../stores/useThemeStore";
import { typedSxMap } from "../../utils/typedSxMap";
import { getStatusBadge } from "../pipelineImports/utils/pipelineImportsTable";
import OverviewSection from "./components/OverviewSection";
import OverviewTable from "./components/OverviewTable";
import ReviewDataTable, { ReviewDataTableRef } from "./components/ReviewDataTable";
import ReviewRowDetail from "./components/ReviewRowDetail";
import AddColumnForm from "./components/addColumnForm";
import CompletePipelineImportForm from "./components/completePipelineImportForm";
import { breakpoints } from "./components/utils/breakpoints";

enum TABS {
  OVERVIEW = 0,
  MAPPING = 1,
  DATA = 2,
}

const defaultOptions: Option[] = [
  { label: "All (0)", filterValue: "all", selected: true },
  { label: "Valid (0)", filterValue: "valid", selected: false },
  {
    label: "Invalid (0)",
    filterValue: "error",
    selected: false,
  },
];

const processingOptions: Option[] = [
  ...defaultOptions,
  {
    label: "Accepted (0)",
    filterValue: "accepted",
    selected: false,
  },
  {
    label: "Rejected (0)",
    filterValue: "rejected",
    selected: false,
  },
];

const styles = typedSxMap({
  container: {
    flexGrow: 1,
    height: "calc(100% + var(--m))",
    display: "flex",
    flexDirection: "column",
    marginBottom: "-30px",
  },
  imageContainer: {
    position: "relative",
    flex: 2,
  },
  divider: {
    mt: 4,
    mb: 2,
    borderColor: "var(--color-border)",
    borderBottomWidth: "1px",
    borderStyle: "solid",
    marginBottom: 0,
  },
  title: {
    display: "flex",
    justifyContent: "space-between",
    width: "100%",
    flexDirection: "column",
    alignItems: "flex-start",
    gap: "var(--m-xs)",
    [breakpoints.md]: {
      flexDirection: "row",
      gap: "var(--m-xs)",
    },
  },
  mainText: {
    display: "flex",
    gap: "10px",
    alignItems: "center",
    width: "100%",
    "& span": {
      maxWidth: "calc(100% - 81px)",
    },
    [breakpoints.md]: {
      width: "calc(100% - 435px)",
    },
  },
  headingTitle: {
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  mainActions: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    flex: 1,
    overflow: "auto",
    justifyContent: "flex-end",
    alignSelf: "flex-end",
  },
  headingWrapper: {
    marginBottom: "1rem",
  },
  titleWithOpenSidebar: {
    flexDirection: "column",
    gap: "var(--m)",
    alignItems: "flex-start",

    "& > :last-child": {
      alignSelf: "flex-end",
    },
  },
  tableContainer: {
    display: "flex",
    flexDirection: "column",
    height: "100%",
    paddingBottom: "var(--m-xxs)",
    "&>div:first-of-type": {
      margin: "0 0 var(--m-s) 0",
    },
  },

  tooltipWrapper: {
    backgroundColor: "var(--color-background-modal)",
    padding: "var(--m-xxs) var(--m-xs)",
    borderRadius: "var(--border-radius)",
    marginTop: "var(--m-xs)",
    boxShadow: "0 0 0 1px var(--color-border), 0 5px 15px rgba(0, 0, 0, 0.2)",
    maxWidth: "300px",
    color: "var(--color-text)",
  },
  mappingModal: {
    minHeight: "40rem",
    minWidth: "30rem",
    "& h2": {
      marginBottom: "40px",
    },
  },
  tabs: {
    height: "100%",
    display: "flex",
    flexDirection: "column",
  },
  tabPanels: {
    flexGrow: 1,
  },
  tabPanel: {
    height: "100%",
    padding: "var(--m-s) 0 0 0",
  },

  tabList: {
    borderBottom: "1px solid var(--color-border)",
    "& button": {
      color: "var(--color-text-soft)",
      padding: "var(--m-xxs) var(--m-s)",
      "&:hover": {
        color: "var(--color-text)",
        borderColor: "var(--color-primary)",
      },
      "&[aria-selected=true]": {
        color: "var(--color-text)",
        borderColor: "var(--color-primary)",
        backgroundColor: "transparent",
      },
    },
  },
});

export default function PipelineImport() {
  const filter = useRef<QueryFilter>("all");
  const theme = useThemeStore((state) => state.theme);
  const queryClient = useQueryClient();
  const message = useWebSocketMessage();
  const [filterOptions, setFilterOptions] = useState<Option[]>(defaultOptions);
  const [updatedRow, setUpdatedRow] = useState<PipelineImportRow | null>(null);
  const { importId } = useParams();
  const { data: pipelineImport, isLoading, refetch } = useGetPipelineImport(importId ?? "");
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [selectedRows, setSelectedRows] = useState<PipelineImportRow[] | null>(null);
  const [columns] = useState<any>([]);
  const [columnLabels, setColumnLabels] = useState<{ key: string; value: string }[]>([]);
  const [selectedTab, setSelectedTab] = useState<TABS>(TABS.OVERVIEW);
  const gridApiRef = useRef<ReviewDataTableRef | null>(null);
  const [dstSchema, setDstSchema] = useState(pipelineImport?.dst_schema);
  const { modal } = useEntitySelection();
  const mappingModal = useModal();
  const addColumnModal = useModal();

  const hasMappingOrRequiredOrDefaultValue = (d: DstSchemaItem) => {
    // Rare case of non-strict equality check being correct
    if (d.mapping_result?.rule?.source_columns?.[0] != null || d.default_value) {
      return true;
    }
    return d.validations?.some((v) => v.validate === "not_blank") || false;
  };

  useEffect(() => {
    const fetchData = async () => {
      if (message?.data === "refresh") {
        await queryClient.invalidateQueries(["pipeline-import", pipelineImport?.id]);
      }
    };
    fetchData();
  }, [message, queryClient]);

  useEffect(() => {
    if (pipelineImport?.id) {
      setDstSchema(
        pipelineImport.dst_schema?.sort((a, b) => {
          const hasMappingOrRequiredOrDefaultValueA = hasMappingOrRequiredOrDefaultValue(a);
          const hasMappingOrRequiredOrDefaultValueB = hasMappingOrRequiredOrDefaultValue(b);
          if (hasMappingOrRequiredOrDefaultValueA && !hasMappingOrRequiredOrDefaultValueB) return -1;
          if (!hasMappingOrRequiredOrDefaultValueA && hasMappingOrRequiredOrDefaultValueB) return 1;
          return 0;
        })
      );
    }
  }, [JSON.stringify(pipelineImport)]);

  useEffect(() => {
    updateFilterOptions(
      filter.current,
      pipelineImport
        ? {
            all: pipelineImport?.dst_num_rows || 0,
            valid: pipelineImport?.dst_num_valid_rows || 0,
            error: pipelineImport?.dst_num_error_rows || 0,
            accepted: pipelineImport?.dst_row_status?.accepted || 0,
            rejected: pipelineImport?.dst_row_status?.failed || 0,
          }
        : undefined
    );
  }, [JSON.stringify(pipelineImport)]);

  const handleCloseDrawer = () => {
    setIsDrawerOpen(false);
    setSelectedRows(null);
  };

  const handleSelectRows = (rows: PipelineImportRow[] | null) => {
    setSelectedRows(rows);
    setIsDrawerOpen(!!rows);
  };

  const handleUpdateRowFromTable = (row: PipelineImportRow | null) => {
    setSelectedRows(row ? [row] : null);
  };

  const updateFilterOptions = useCallback(
    (option: string, counts?: FilterOptionCounts & { accepted?: number; rejected?: number }) => {
      filter.current = option as QueryFilter;
      const currentOptions = ["importing", "complete"].includes(pipelineImport?.status || "") ? processingOptions : defaultOptions;

      setFilterOptions(
        currentOptions.map((filterOption) => {
          if (counts) {
            filterOption.label = `${filterOption.label.split(" ")[0]} `;
            if (filterOption.filterValue === "accepted") {
              filterOption.count = pipelineImport?.dst_row_status?.accepted || 0;
            } else if (filterOption.filterValue === "rejected") {
              filterOption.count = pipelineImport?.dst_row_status?.failed || 0;
            } else {
              filterOption.count = counts[filterOption.filterValue] || 0;
            }
          }
          return {
            ...filterOption,
            selected: filterOption.filterValue === option,
          };
        })
      );
    },
    [pipelineImport?.status, pipelineImport?.dst_row_status]
  );

  const getTitle = (pipelineImport?: PipelineImportType) => {
    return `${pipelineImport?.name} - Import`;
  };

  const handleUpdateRow = (row: PipelineImportRow) => {
    setUpdatedRow(row);
    refetch();
  };

  const importTitle: ReactNode = (
    <Heading size="m" sx={styles.headingTitle} as="h1">
      {getTitle(pipelineImport)}
    </Heading>
  );

  const download = (importItem?: PipelineImportType, fileType: string = "original") => {
    downloadFile(importItem?.id || "", fileType);
  };

  const handleKeyDownUp = (event: any) => {
    const selectedRowIndex = selectedRows?.[0]?.rowIndex || 0;
    const nextRowIndex = event.key === "ArrowDown" ? selectedRowIndex + 1 : selectedRowIndex - 1;
    gridApiRef.current?.navigateToNextCell({
      key: event.key,
      nextCellPosition: { rowIndex: nextRowIndex },
      columns: columns,
      api: gridApiRef?.current?.api as GridApi,
    });
  };

  // Un-comment to close draw on filter change
  // useEffect(() => {
  //   if (filter.current) {
  //     handleCloseDrawer();
  //   }
  // }, [filter.current]);

  if (isLoading) {
    return null;
  }

  return (
    <>
      <Box sx={styles.container}>
        <Box sx={styles.headingWrapper}>
          <Box
            sx={{
              ...styles.title,
            }}>
            <Box sx={styles.mainText}>
              {getTitle(pipelineImport).length > 50 ? (
                <Tooltip label={getTitle(pipelineImport)} shouldWrapChildren={true} sx={styles.tooltipWrapper}>
                  {importTitle}
                </Tooltip>
              ) : (
                <>{importTitle}</>
              )}
              {getStatusBadge(pipelineImport?.status, pipelineImport?.error || "")}
            </Box>
            <Box sx={styles.mainActions}>
              <DownloadMenu
                buttonLabel="Download"
                options={[
                  {
                    label: "All Rows",
                    onClick: () => download(pipelineImport, "all"),
                  },
                  {
                    label: "Valid Rows",
                    onClick: () => download(pipelineImport, "valid"),
                  },
                  {
                    label: "Invalid Rows",
                    onClick: () => download(pipelineImport, "error"),
                  },
                  ...(["importing", "complete"].includes(pipelineImport?.status || "")
                    ? [
                        {
                          label: "Accepted Rows",
                          onClick: () => download(pipelineImport, "accepted"),
                        },
                        {
                          label: "Rejected Rows",
                          onClick: () => download(pipelineImport, "rejected"),
                        },
                      ]
                    : []),
                  {
                    label: "Original File",
                    onClick: () => download(pipelineImport, "original"),
                  },
                ]}
              />
              <Button
                variants={["secondary"]}
                onClick={() => {
                  if (pipelineImport?.drive_file_id) {
                    window.open(`https://docs.google.com/spreadsheets/d/${pipelineImport?.drive_file_id}`, "_blank");
                  }
                }}
                disabled={!pipelineImport?.drive_file_id}>
                Open Sheets
              </Button>
              <Button
                variants={["primary"]}
                onClick={() => modal.setOpen(true)}
                disabled={isLoading || pipelineImport?.status === "importing" || pipelineImport?.status === "complete"}>
                Start Import
              </Button>
            </Box>
          </Box>
        </Box>
        <Tabs
          sx={styles.tabs}
          index={selectedTab}
          onChange={(tabIndex) => {
            setIsDrawerOpen(false);
            setSelectedTab(tabIndex as TABS);
          }}>
          <TabList sx={styles.tabList}>
            <Tab>Overview</Tab>
            <Tab>Mapping</Tab>
            <Tab>Data</Tab>
          </TabList>
          <TabPanels sx={styles.tabPanels}>
            <TabPanel sx={styles.tabPanel}>{pipelineImport && <OverviewSection pipelineImport={pipelineImport} />}</TabPanel>
            <TabPanel sx={styles.tabPanel}>
              <OverviewTable srcSchema={pipelineImport?.src_schema} dstSchema={dstSchema}></OverviewTable>
            </TabPanel>
            <TabPanel sx={styles.tabPanel}>
              <Box
                sx={styles.tableContainer}
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === "ArrowDown" || e.key === "ArrowUp") {
                    handleKeyDownUp(e);
                  }
                }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "var(--m-s)" }}>
                  <ToggleFilter options={filterOptions} onChange={(option: string) => updateFilterOptions(option)} />
                  <Button
                    variants={["secondary"]}
                    onClick={() => addColumnModal.setOpen(true)}
                    disabled={isLoading || pipelineImport?.status === "importing" || pipelineImport?.status === "complete"}>
                    Add Column
                  </Button>
                </Box>
                {columns && (
                  <ReviewDataTable
                    ref={gridApiRef}
                    pipelineImport={pipelineImport}
                    onSelectRows={handleSelectRows}
                    importId={importId}
                    filter={filter.current}
                    theme={theme}
                    isVisible={selectedTab === TABS.DATA}
                    updatedRow={updatedRow}
                    onUpdateRow={handleUpdateRowFromTable}
                    importStatus={pipelineImport?.status}
                    onColumnLabelsChange={setColumnLabels}
                  />
                )}
                <ReviewRowDetail
                  isOpen={isDrawerOpen}
                  onClose={handleCloseDrawer}
                  importId={pipelineImport?.id}
                  rows={selectedRows}
                  columns={columnLabels}
                  onUpdateRow={handleUpdateRow}
                  isReadOnly={pipelineImport?.status !== "review"}
                  srcSchema={pipelineImport?.src_schema}
                  dstSchema={dstSchema}
                  onBulkEdit={() => {
                    refetch().then(() => {
                      queryClient.setQueryData(["pipeline-import", importId], (oldData: any) => ({
                        ...oldData,
                        forceRefresh: true,
                      }));
                    });
                  }}
                />
              </Box>
            </TabPanel>
          </TabPanels>
        </Tabs>
      </Box>
      {modal.openDelayed && (
        <Modal {...modal} useBox={false} useCloseButton>
          <CustomBox>
            <CompletePipelineImportForm
              pipelineImport={pipelineImport}
              onSuccess={() => {
                setSelectedTab(TABS.OVERVIEW);
                modal.handleClose();
              }}
              onClose={modal.handleClose}></CompletePipelineImportForm>
          </CustomBox>
        </Modal>
      )}
      {mappingModal.openDelayed && (
        <Modal {...mappingModal} useBox={true} useCloseButton>
          <Box sx={styles.mappingModal}>
            <h2>Column Mapping</h2>
            {dstSchema?.map((col, index) => (
              <div key={index}>
                <h3 style={{ marginTop: "10px" }}>{col?.name}</h3>
                {col.mapping_result?.options?.map((option: any, i: any) => (
                  <pre style={{ fontFamily: "monospace" }} key={i}>
                    {JSON.stringify(option)}
                  </pre>
                ))}
              </div>
            ))}
          </Box>
        </Modal>
      )}
      {addColumnModal.openDelayed && (
        <Modal {...addColumnModal} useBox={false} useCloseButton>
          <CustomBox variants={["fluid", "space-mid"]} style={{ width: "450px" }}>
            <AddColumnForm
              pipelineImport={pipelineImport}
              onSuccess={() => {
                addColumnModal.handleClose();
                refetch().then(() => {
                  queryClient.setQueryData(["pipeline-import", importId], (oldData: any) => ({
                    ...oldData,
                    forceRefresh: true,
                  }));
                });
              }}
            />
          </CustomBox>
        </Modal>
      )}
    </>
  );
}
